import { type StateCreator } from 'zustand';

import { type UseTrackCanvasCreatedParams, type UseTrackCanvasViewedParams } from '@air/analytics';
import { type AppNode } from '@air/feature-edit-canvas';

import { type BoardViewCollapseSlice } from '~/components/v2/store/shared/createBoardViewCollapseSlice';
import { type WorkspaceRightPanelCanvasSlice } from '~/components/v2/store/shared/createWorkspaceRightPanelCanvasSlice';

export type DraftCanvasAnalyticsContext = {
  source: UseTrackCanvasCreatedParams['source'];
  board_id?: string;
  edit_in_canvas_action?: UseTrackCanvasCreatedParams['edit_in_canvas_action'];
  library_id?: string;
  type?: UseTrackCanvasCreatedParams['type'];
  announcement_id?: UseTrackCanvasCreatedParams['announcement_id'];
};

export type DraftCanvasState = {
  /** Minted by `openDraftCanvas` — one draft's identity across in-place field updates */
  openKey?: string;
  boardId?: string;
  analyticsContext: DraftCanvasAnalyticsContext;
  /** Pre-built nodes to add when the canvas editor mounts */
  initialNodes?: AppNode[];
  /** Toast message to show after initial nodes are added */
  insertionToastMessage?: string | null;
  /** Attribution for the canvas_viewed event fired when the draft commits */
  viewedFrom?: UseTrackCanvasViewedParams['viewed_from'];
  /** Clip of the asset this draft was opened from. Commit hands it to the saved canvas's session link. */
  versionTargetClipId?: string;
};

export type DraftCanvasSlice = {
  draftCanvas: DraftCanvasState | null;
  /** Asset-modal URL to reopen on close. Off the draft: commit clears the draft before Esc. */
  canvasReturnTo: string | null;
  /** Transient flag set during draft→real transition to prevent store clearing */
  committingDraft: boolean;
  /** Close/leave during an in-flight ui2026 commit — the pending replace must not reopen the editor */
  skipPostCommitNavigation: boolean;
  setDraftCanvas: (draft: DraftCanvasState | null) => void;
  setCanvasReturnTo: (href: string | null) => void;
  setCommittingDraft: (committing: boolean) => void;
  setSkipPostCommitNavigation: (skip: boolean) => void;
  /**
   * One update for "Create a canvas": drop the previous selection, park the draft,
   * and collapse the board list when the draft is empty so the hero is what mounts.
   */
  startDraftCanvas: (draft: DraftCanvasState) => void;
};

type DraftCanvasHostState = DraftCanvasSlice &
  Pick<WorkspaceRightPanelCanvasSlice, 'selectedCanvasId' | 'canvasViewedFrom'> &
  Pick<BoardViewCollapseSlice, 'boardViewCollapsed'>;

export type CreateDraftCanvasSlice = () => StateCreator<DraftCanvasHostState, [], [], DraftCanvasSlice>;

export const createDraftCanvasSlice: CreateDraftCanvasSlice = () => (set) => ({
  draftCanvas: null,
  canvasReturnTo: null,
  committingDraft: false,
  skipPostCommitNavigation: false,
  setDraftCanvas: (draft: DraftCanvasState | null) => {
    set({ draftCanvas: draft });
  },
  setCanvasReturnTo: (href: string | null) => {
    set({ canvasReturnTo: href });
  },
  setCommittingDraft: (committing: boolean) => {
    set({ committingDraft: committing });
  },
  setSkipPostCommitNavigation: (skip: boolean) => {
    set({ skipPostCommitNavigation: skip });
  },
  startDraftCanvas: (draft) => {
    set({
      selectedCanvasId: null,
      canvasViewedFrom: undefined,
      draftCanvas: draft,
      // Seeded drafts (Edit in canvas, selection bar) are not empty — collapsing would
      // flash the scatter hero and hide the board list over content.
      boardViewCollapsed: !draft.initialNodes?.length,
    });
  },
});
